/**
 * PatientPlansIndexPage (Phase 2.8 Commit 9)
 *
 * Mounted at /org/patient-plans. Clinic-wide list of attached patient
 * plans with filter chips (status / scheduled today / my patients) and
 * a patient-name search. Each row links to PatientPlanPage.
 *
 * Reads from `usePatientPlanStore.fetchPlanIndex(filters)` which calls
 * GET /plans/index. Doctor sees all plans by default; the "My patients"
 * chip filters to plans they own.
 */
import React, { useEffect, useMemo, useState } from 'react';

import usePatientPlanStore from '../stores/patientPlanStore';
import { useAuth } from '../context/AuthContext';
import { ROUTES } from '../constants/routes';

const STATUS_OPTIONS = ['active', 'completed', 'cancelled'];

const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};


export default function PatientPlansIndexPage() {
  const { user } = useAuth() || {};
  const myMemberId = user?.member_id || user?.memberId;
  const myRole = String(user?.role || '').toUpperCase();

  const rows = usePatientPlanStore((s) => s.planIndexRows);
  const isLoading = usePatientPlanStore((s) => s.isLoadingIndex);
  const error = usePatientPlanStore((s) => s.error);
  const fetchPlanIndex = usePatientPlanStore((s) => s.fetchPlanIndex);

  const [statusFilter, setStatusFilter] = useState('active');
  const [scheduledToday, setScheduledToday] = useState(false);
  const [myPatientsOnly, setMyPatientsOnly] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const filters = {};
    if (statusFilter) filters.status = statusFilter;
    if (scheduledToday) filters.scheduled_on = todayIso();
    if (myPatientsOnly && myMemberId) filters.owner_doctor_member_id = myMemberId;
    if (search.trim()) filters.q = search.trim();
    fetchPlanIndex(filters);
  }, [statusFilter, scheduledToday, myPatientsOnly, search, myMemberId, fetchPlanIndex]);

  const isDoctor = myRole === 'DOCTOR';

  const visibleRows = useMemo(() => rows || [], [rows]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-5 text-slate-900 dark:text-slate-100">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-baseline gap-3">
          <h1 className="text-lg font-bold text-indigo-900 dark:text-indigo-100">
            📋 Patient plans
          </h1>
          <span className="text-xs text-slate-600 dark:text-slate-300">
            {visibleRows.length} {visibleRows.length === 1 ? 'plan' : 'plans'}
          </span>
        </div>

        {/* Filter chips + search */}
        <div className="rounded-xl border-2 border-indigo-200 dark:border-indigo-800 bg-white dark:bg-slate-900 p-3 space-y-2">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
              Status:
            </span>
            {STATUS_OPTIONS.map((st) => (
              <button
                key={st}
                type="button"
                onClick={() => setStatusFilter(st)}
                className={
                  'text-xs font-medium px-2.5 py-1 rounded-full border '
                  + (statusFilter === st
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700')
                }
              >
                {st}
              </button>
            ))}
            <span className="ml-2 w-px h-4 bg-slate-300 dark:bg-slate-600" />
            <button
              type="button"
              onClick={() => setScheduledToday((v) => !v)}
              className={
                'text-xs font-medium px-2.5 py-1 rounded-full border '
                + (scheduledToday
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700')
              }
            >
              Scheduled today
            </button>
            {isDoctor && (
              <button
                type="button"
                onClick={() => setMyPatientsOnly((v) => !v)}
                className={
                  'text-xs font-medium px-2.5 py-1 rounded-full border '
                  + (myPatientsOnly
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700')
                }
              >
                My patients
              </button>
            )}
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search patient name…"
            className="w-full text-sm px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder:text-slate-400"
          />
        </div>

        {error && (
          <div className="text-sm text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-200 border border-red-300 rounded-md p-2">
            {error}
          </div>
        )}

        {/* Rows */}
        <div className="rounded-xl border-2 border-indigo-200 dark:border-indigo-800 bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-700">
          {isLoading && visibleRows.length === 0 && (
            <div className="text-sm text-slate-600 dark:text-slate-300 italic p-4">
              Loading patient plans…
            </div>
          )}
          {!isLoading && visibleRows.length === 0 && (
            <div className="text-sm text-slate-600 dark:text-slate-300 italic p-4">
              No plans match the current filters.
            </div>
          )}
          {visibleRows.map((row) => (
            <button
              key={row.plan_id}
              type="button"
              onClick={() => window.open(
                `${ROUTES.PATIENT_PLANS}/${row.plan_id}`,
                '_blank',
                'noopener',
              )}
              className="w-full text-left px-4 py-3 hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
            >
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-sm font-semibold text-indigo-900 dark:text-indigo-100">
                  {row.patient_name || row.patient_id}
                </span>
                <span className="text-xs text-slate-700 dark:text-slate-200">
                  · {row.plan_title}
                </span>
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-100">
                  {row.status}
                </span>
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200">
                  {row.payment_mode === 'upfront'
                    ? (row.payment_status === 'upfront_paid'
                      ? 'Upfront paid'
                      : 'Upfront due')
                    : 'Per visit'}
                </span>
                <span className="ml-auto text-xs text-slate-700 dark:text-slate-200">
                  {row.step_count} steps · ₹{Number(row.total_estimate || 0).toLocaleString('en-IN')}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
